import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import type { QuizState } from '@/types/quiz';

interface AdaptiveDifficultyOptions {
  // Minimum number of questions before making an adjustment
  minQuestionsBeforeAdjustment?: number;
  // Whether to show notifications on difficulty change
  showNotifications?: boolean;
  // Number of recent questions to consider
  recentQuestionsWindow?: number;
  // Maximum time (ms) for a "fast" answer
  fastAnswerThresholdMs?: number;
  // Start with medium difficulty
  startWithMedium?: boolean;
  // Score multipliers for different difficulties
  scoreMultipliers?: {
    easy: number;
    medium: number;
    hard: number;
  };
}

export function useRealTimeAdaptiveDifficulty(
  state: QuizState,
  setState: (state: QuizState | ((prev: QuizState) => QuizState)) => void,
  options: AdaptiveDifficultyOptions = {}
) {
  // Default options
  const {
    minQuestionsBeforeAdjustment = 1,
    showNotifications = true,
    recentQuestionsWindow = 5,
    fastAnswerThresholdMs = 10000, // 10 seconds
    startWithMedium = true,
    scoreMultipliers = { easy: 1, medium: 2, hard: 3 }
  } = options;

  // Track correct/incorrect answers
  const [correctAnswers, setCorrectAnswers] = useState<boolean[]>([]);
  // Track answer times (in ms)
  const [answerTimes, setAnswerTimes] = useState<number[]>([]);
  // Time when the current question was presented
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  // Track if initial difficulty set to medium
  const [initialDifficultySet, setInitialDifficultySet] = useState(false);
  // Prevent multiple adjustments for the same question
  const [lastAdjustmentQuestionIndex, setLastAdjustmentQuestionIndex] = useState(-1);

  // Initialize difficulty to medium right away if startWithMedium is true
  useEffect(() => {
    if (startWithMedium && !initialDifficultySet) {
      console.log("Setting initial difficulty to medium");
      setState(prev => ({
        ...prev,
        difficulty: "medium"
      }));
      setInitialDifficultySet(true);
    }
  }, [startWithMedium, initialDifficultySet, setState]);

  // Reset tracking when subject or category changes
  useEffect(() => {
    console.log("Resetting adaptive difficulty tracking due to subject/category change");
    setCorrectAnswers([]);
    setAnswerTimes([]);
    setQuestionStartTime(Date.now());
    setInitialDifficultySet(false);
    setLastAdjustmentQuestionIndex(-1);
    
    // Reset difficulty to medium when subject changes if startWithMedium is true
    if (startWithMedium) {
      setState(prev => ({
        ...prev,
        difficulty: "medium"
      }));
      setInitialDifficultySet(true);
      console.log("Subject/category changed: Reset difficulty to medium");
    }
  }, [state.subject, state.category, startWithMedium, setState]);

  // Set question start time when question index changes
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [state.questionIndex]);

  const recordAnswer = useCallback((isCorrect: boolean) => {
    const now = Date.now();
    const answerTime = now - questionStartTime;
    const isFast = answerTime < fastAnswerThresholdMs;
    
    console.log(`Answer recorded: ${isCorrect ? 'Correct' : 'Incorrect'}, Time: ${answerTime}ms, ${isFast ? 'Fast' : 'Slow'}`);
    console.log(`Current difficulty before evaluation: ${state.difficulty}`);
    
    // Record answer correctness
    setCorrectAnswers(prev => {
      const updated = [...prev, isCorrect];
      // Keep only the most recent window
      return updated.slice(-recentQuestionsWindow);
    });
    
    // Record answer time
    setAnswerTimes(prev => {
      const updated = [...prev, answerTime];
      // Keep only the most recent window
      return updated.slice(-recentQuestionsWindow);
    });

    // Update question start time for next question
    setQuestionStartTime(now);

    // Determine score multiplier based on current difficulty
    const scoreMultiplier = scoreMultipliers[state.difficulty] || 1;

    // Apply score adjustment based on difficulty
    if (isCorrect) {
      setState(prev => ({
        ...prev,
        score: prev.score + scoreMultiplier
      }));
    }

    // Calculate performance metrics for potential difficulty adjustment
    evaluateDifficultyAdjustment(isCorrect, isFast);
  }, [questionStartTime, recentQuestionsWindow, fastAnswerThresholdMs, 
      scoreMultipliers, state.difficulty, setState]);

  // Evaluate if difficulty should be adjusted based on current performance
  const evaluateDifficultyAdjustment = useCallback((isCorrect: boolean, isFast: boolean) => {
    // Prevent multiple adjustments for the same question
    if (lastAdjustmentQuestionIndex === state.questionIndex) {
      console.log('Skipping difficulty adjustment - already adjusted for this question');
      return;
    }

    let newDifficulty = state.difficulty;
    let adjustmentReason = "";

    // Log the current state for debugging
    console.log('Evaluating difficulty adjustment:', {
      isCorrect,
      isFast,
      currentDifficulty: state.difficulty,
      questionIndex: state.questionIndex
    });

    // SIMPLIFIED LOGIC - Only immediate response-based adjustments
    // Case 1: Correct and Fast - Increase difficulty
    if (isCorrect && isFast) {
      if (state.difficulty === "easy") {
        newDifficulty = "medium";
        adjustmentReason = "correct and fast on easy";
      } else if (state.difficulty === "medium") {
        newDifficulty = "hard";
        adjustmentReason = "correct and fast on medium";
      } 
      // If already hard, stay at hard
    }
    // Case 2: Incorrect and Slow - Decrease difficulty
    else if (!isCorrect && !isFast) {
      if (state.difficulty === "hard") {
        newDifficulty = "medium";
        adjustmentReason = "incorrect and slow on hard";
      } else if (state.difficulty === "medium") {
        newDifficulty = "easy";
        adjustmentReason = "incorrect and slow on medium";
      }
      // If already easy, stay at easy
    }
    // Case 3: Correct but Slow - Maintain current difficulty
    else if (isCorrect && !isFast) {
      adjustmentReason = "correct but slow - maintaining difficulty";
    }
    // Case 4: Incorrect but Fast - Decrease difficulty (careless mistake)
    else if (!isCorrect && isFast) {
      if (state.difficulty === "hard") {
        newDifficulty = "medium";
        adjustmentReason = "incorrect even though fast on hard";
      } else if (state.difficulty === "medium") {
        newDifficulty = "easy";
        adjustmentReason = "incorrect even though fast on medium";
      }
      // If already easy, stay at easy
    }
    
    // If difficulty should change
    if (newDifficulty !== state.difficulty) {
      console.log(`Adjusting difficulty from ${state.difficulty} to ${newDifficulty} due to ${adjustmentReason}`);
      
      setState(prev => ({
        ...prev,
        difficulty: newDifficulty
      }));
      
      // Mark this question index as having been adjusted
      setLastAdjustmentQuestionIndex(state.questionIndex);
      
      if (showNotifications) {
        if (newDifficulty > state.difficulty) {
          toast.success(`Difficulty increased to ${newDifficulty} – great job!`);
        } else {
          toast.info(`Taking it slower with ${newDifficulty} difficulty – let's review together.`);
        }
      }
    } else {
      console.log(`No difficulty change needed. Staying at ${state.difficulty} - ${adjustmentReason}`);
    }
  }, [state.difficulty, state.questionIndex, setState, 
      showNotifications, lastAdjustmentQuestionIndex]);

  // Return utility functions and current state
  return {
    recordCorrectAnswer: () => recordAnswer(true),
    recordIncorrectAnswer: () => recordAnswer(false),
    currentDifficulty: state.difficulty,
    scoreMultiplier: scoreMultipliers[state.difficulty] || 1
  };
}
